import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Pencil } from 'lucide-react';
import { Table, TableBody, TableCell, TableRow } from '../ui/table';
import { LeadCardEditor, type LeadCardDraft } from './LeadCardEditor';
import { LeadCardSummary } from './LeadCardSummary';
import { RefreshFlash } from './RefreshFlash';
import { leadCardSignature } from './refresh-flash';
import { RoundClimbThumbnail } from './RoundClimbThumbnail';
import { byBlockName } from './blockOrder';
import { leadStyleLabelKey } from './lead-climb-style';
import { cn } from '../../lib/utils';
import type { LeadRoundResult, RoundClimb } from '../../types/competition-round';

interface ParticipantLeadRoutesTableProps {
  roundClimbs: RoundClimb[];
  /** Whether the round still takes the athlete's cards: running, or in its corrections. */
  scoringOpen: boolean;
  /** Writes one route's card. Called with the draft as it stands. */
  onSave: (roundClimb: RoundClimb, draft: LeadCardDraft) => void | Promise<void>;
  /** The route whose save is in flight, so only its own button goes quiet. */
  saving?: string | null;
}

const cardOf = (roundClimb: RoundClimb) => roundClimb.my_result as LeadRoundResult | null;

/**
 * The athlete's own routes in one lead round, table-shaped like the judge's
 * scoresheet: tap a route to open its card, one at a time.
 *
 * The counterpart of `ParticipantBoulderBlocksTable`, and the discipline makes the
 * same single difference it makes everywhere: there are no goes to press, there is
 * a hold reached and a time taken. A route already climbed is finished business —
 * one go is all there is — so it goes grey and sinks under a "Completed" divider,
 * out of the way of what is still to come.
 *
 * A route that needs a judge has nothing here to tap: it shows read-only, whatever
 * the judge has written on it.
 */
export function ParticipantLeadRoutesTable({
  roundClimbs,
  scoringOpen,
  onSave,
  saving,
}: ParticipantLeadRoutesTableProps) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  const active = roundClimbs.filter((rc) => !cardOf(rc)).sort(byBlockName);
  const completed = roundClimbs.filter((rc) => cardOf(rc)).sort(byBlockName);

  const renderRow = (roundClimb: RoundClimb) => {
    const card = cardOf(roundClimb);
    const writable = !roundClimb.requires_judge && scoringOpen;
    const open = openId === roundClimb.id && writable;

    const select = () => {
      if (writable) setOpenId(roundClimb.id);
    };

    return (
      <Fragment key={roundClimb.id}>
        <TableRow
          className={cn('border-0', card ? 'text-muted-foreground' : writable && 'cursor-pointer')}
          data-state={open ? 'selected' : undefined}
          data-testid="route-row"
          onClick={select}
        >
          <TableCell className="py-2">
            <div className="flex items-center gap-3">
              <RoundClimbThumbnail roundClimb={roundClimb} className="h-12 w-12" />
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{roundClimb.display_name}</span>
                {/* How many holds there are to get: what the number typed below
                    is read against. */}
                {roundClimb.holds_count != null && (
                  <Badge variant="outline" className="text-xs">
                    {t('leadRounds.holdsOnRoute', { count: roundClimb.holds_count })}
                  </Badge>
                )}
                {/* A route taken out of the choice, where the class has one. */}
                {roundClimb.required_climb_style && (
                  <Badge variant="outline" className="text-xs">
                    {t('leadRounds.style.forced', {
                      style: t(leadStyleLabelKey(roundClimb.required_climb_style)),
                    })}
                  </Badge>
                )}
                {roundClimb.requires_judge && (
                  <Badge variant="outline" className="border-blue-600 text-xs text-blue-600">
                    {t('competitionRounds.requiresJudge')}
                  </Badge>
                )}
              </div>
            </div>
          </TableCell>
        </TableRow>

        <TableRow
          className={cn(open && 'border-0', writable && 'cursor-pointer')}
          data-state={open ? 'selected' : undefined}
          data-testid="route-card-row"
          onClick={select}
        >
          <TableCell className="pb-2 pt-0">
            <div className="flex items-center gap-2">
              {/* Lit when the page's refresh brings a card a judge wrote. */}
              <RefreshFlash value={leadCardSignature(card)}>
                <LeadCardSummary
                  holds={card?.holds_count}
                  timeSeconds={card?.time_seconds}
                  formattedTime={card?.formatted_time}
                  topped={card?.topped}
                  climbedAs={card?.climbed_as}
                />
              </RefreshFlash>

              {writable && card && !open && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0"
                  aria-label={t('leadRounds.editCard')}
                  data-testid="route-edit-button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenId(roundClimb.id);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>

        {open && (
          <TableRow data-state="selected" data-testid="route-editor-row">
            <TableCell className="pb-3 pt-0">
              <LeadCardEditor
                holds={card?.holds_count ?? null}
                timeSeconds={card?.time_seconds ?? null}
                climbedAs={card?.climbed_as}
                // The server works these out for the athlete whose page this
                // is: their class's setting, or the route's forcing over it.
                styles={roundClimb.permitted_climb_styles ?? ['lead']}
                maxHolds={roundClimb.holds_count}
                stopwatch
                saving={saving === roundClimb.id}
                onSave={(draft) => onSave(roundClimb, draft)}
                data-testid="route-card-editor"
              />
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  };

  if (roundClimbs.length === 0) {
    return (
      <p className="py-8 text-center text-muted-foreground">{t('competitionRounds.noClimbs')}</p>
    );
  }

  return (
    <Table>
      <TableBody>
        {active.map(renderRow)}

        {completed.length > 0 && (
          <TableRow className="hover:bg-transparent" data-testid="completed-divider">
            <TableCell className="pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('competitionRounds.completedBlocks', { defaultValue: 'Completed' })}
            </TableCell>
          </TableRow>
        )}

        {completed.map(renderRow)}
      </TableBody>
    </Table>
  );
}
